import type {
  ResourceRoomProjectDetail,
  ResourceRoomProjectSummary
} from "../features/projects/resourceRoomTypes";
import {
  commitCandidate,
  executeDocumentCommand,
  exportDocument,
  scheduleGlobal,
  searchCandidates,
  viewTimetable,
  type SchedulingCandidatesResponse,
  type TimetableViewResponse,
  type ExportDownload
} from "../api/statelessClient";
import {
  createEmptyProjectDocument,
  normalizeProjectName,
  projectDetail,
  projectSummary,
  renameProjectDocument,
  type ResourceRoomProjectDocumentV2
} from "../storage/resourceRoomDocument";
import {
  openProjectStore,
  ProjectRevisionConflictError,
  type ProjectStore
} from "../storage/projectStore";

export class ProjectRepositoryError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ProjectRepositoryError";
    this.code = code;
  }
}

export class ProjectRepository {
  constructor(private readonly store: ProjectStore) {}

  async list(query = ""): Promise<{ items: ResourceRoomProjectSummary[] }> {
    const documents = await this.store.list(query);
    return { items: documents.map(projectSummary) };
  }

  async create(name: string): Promise<ResourceRoomProjectSummary> {
    await this.requireUniqueName(name);
    const document = createEmptyProjectDocument(name);
    await this.store.putIfRevision(document, null);
    return projectSummary(document);
  }

  async read(projectId: string): Promise<ResourceRoomProjectDetail> {
    return projectDetail(await this.requireDocument(projectId));
  }

  async readDocument(projectId: string): Promise<ResourceRoomProjectDocumentV2> {
    return this.requireDocument(projectId);
  }

  async rename(
    projectId: string,
    name: string,
    expectedDocumentRevision: number
  ): Promise<ResourceRoomProjectSummary> {
    const current = await this.requireDocument(projectId);
    if (current.document_revision !== expectedDocumentRevision) {
      throw new ProjectRevisionConflictError(
        expectedDocumentRevision,
        current.document_revision
      );
    }
    await this.requireUniqueName(name, projectId);
    const updated = renameProjectDocument(current, name);
    await this.store.putIfRevision(updated, current.document_revision);
    return projectSummary(updated);
  }

  async delete(projectId: string): Promise<void> {
    await this.requireDocument(projectId);
    await this.store.delete(projectId);
  }

  async execute<T>(
    projectId: string,
    commandType: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const current = await this.requireDocument(projectId);
    const response = await executeDocumentCommand<T>(
      current,
      commandType,
      payload,
      signal
    );
    if (response.document.id !== current.id) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "计算服务返回了不匹配的课表项目"
      );
    }
    if (response.mutated) {
      if (response.document.document_revision !== current.document_revision + 1) {
        throw new ProjectRepositoryError(
          "DOCUMENT_INVALID",
          "计算服务返回了不连续的课表修订号"
        );
      }
      await this.store.putIfRevision(response.document, current.document_revision);
    } else if (response.document.document_revision !== current.document_revision) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "只读操作意外修改了课表修订号"
      );
    }
    return response.result;
  }

  async export(
    projectId: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<ExportDownload> {
    return exportDocument(await this.requireDocument(projectId), payload, signal);
  }

  async searchCandidates(
    projectId: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<SchedulingCandidatesResponse> {
    return searchCandidates(await this.requireDocument(projectId), payload, signal);
  }

  async commitCandidate(
    projectId: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<Record<string, unknown>> {
    const current = await this.requireDocument(projectId);
    const response = await commitCandidate(current, payload, signal);
    if (
      response.document.id !== current.id ||
      response.document.document_revision !== current.document_revision + 1
    ) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "计算服务返回了不连续的课表项目"
      );
    }
    await this.store.putIfRevision(response.document, current.document_revision);
    return response.result;
  }

  async scheduleGlobal(
    projectId: string,
    confirmProtected: boolean,
    signal?: AbortSignal
  ): Promise<Record<string, unknown>> {
    const current = await this.requireDocument(projectId);
    const response = await scheduleGlobal(current, confirmProtected, signal);
    if (
      response.document.id !== current.id ||
      response.document.document_revision !== current.document_revision + 1
    ) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "计算服务返回了不连续的课表项目"
      );
    }
    await this.store.putIfRevision(response.document, current.document_revision);
    return response.result;
  }

  async viewTimetable(
    projectId: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<TimetableViewResponse> {
    return viewTimetable(await this.requireDocument(projectId), payload, signal);
  }

  close(): void {
    this.store.close();
  }

  private async requireDocument(
    projectId: string
  ): Promise<ResourceRoomProjectDocumentV2> {
    const document = await this.store.get(projectId);
    if (!document) {
      throw new ProjectRepositoryError("PROJECT_NOT_FOUND", "课表项目不存在");
    }
    return document;
  }

  private async requireUniqueName(rawName: string, excludedId?: string): Promise<void> {
    const nameKey = normalizeProjectName(rawName).toLocaleLowerCase("zh-CN");
    const existing = await this.store.list();
    if (
      existing.some(
        (document) =>
          document.id !== excludedId &&
          document.name.toLocaleLowerCase("zh-CN") === nameKey
      )
    ) {
      throw new ProjectRepositoryError("NAME_CONFLICT", "课表名称已存在");
    }
  }
}

let repositoryPromise: Promise<ProjectRepository> | null = null;

export function getProjectRepository(): Promise<ProjectRepository> {
  repositoryPromise ??= openProjectStore().then((store) => new ProjectRepository(store));
  return repositoryPromise;
}

export function resetProjectRepositoryForTests(): void {
  if (repositoryPromise) {
    void repositoryPromise.then((repository) => repository.close());
  }
  repositoryPromise = null;
}
